import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { capacityAPI, sedesAPI } from '../../services/api';
import { Settings, Loader2, Save } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const DAYS = [
  { value: 0, label: 'Lunes' },
  { value: 1, label: 'Martes' },
  { value: 2, label: 'Miércoles' },
  { value: 3, label: 'Jueves' },
  { value: 4, label: 'Viernes' },
];

const CapacityPage = () => {
  const [sedes, setSedes] = useState([]);
  const [selectedSede, setSelectedSede] = useState('');
  const [capacities, setCapacities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(
    DAYS.map(d => ({
      day_of_week: d.value,
      max_capacity: 50,
      high_demand_limit_percent: 70,
      is_redistribution_day: false
    }))
  );

  useEffect(() => {
    loadSedes();
  }, []);

  useEffect(() => {
    if (selectedSede) {
      loadCapacity();
    }
  }, [selectedSede]);

  const loadSedes = async () => {
    try {
      const response = await sedesAPI.getAll();
      setSedes(response.data);
      if (response.data.length > 0) {
        setSelectedSede(response.data[0].id);
      }
    } catch (error) {
      console.error('Error loading sedes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCapacity = async () => {
    try {
      const response = await capacityAPI.getAll(selectedSede);
      const existing = response.data;
      
      // Merge with defaults
      const merged = DAYS.map(d => {
        const found = existing.find(e => e.day_of_week === d.value);
        return found || {
          day_of_week: d.value,
          max_capacity: 50,
          high_demand_limit_percent: 70,
          is_redistribution_day: false
        };
      });
      setFormData(merged);
      setCapacities(existing);
    } catch (error) {
      console.error('Error loading capacity:', error);
    }
  };

  const handleChange = (dayIndex, field, value) => {
    setFormData(prev => prev.map((item, i) => 
      i === dayIndex ? { ...item, [field]: value } : item
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const cap of formData) {
        await capacityAPI.create({
          sede_id: selectedSede,
          ...cap
        });
      }
      toast.success('Configuración guardada exitosamente');
      loadCapacity();
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Capacidad Diaria
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Configura el aforo máximo y límites por día de la semana
            </p>
          </div>
          <div className="flex gap-2">
            <Select value={selectedSede} onValueChange={setSelectedSede}>
              <SelectTrigger className="w-[180px] rounded-sm" data-testid="sede-selector">
                <SelectValue placeholder="Seleccionar sede" />
              </SelectTrigger>
              <SelectContent>
                {sedes.map((sede) => (
                  <SelectItem key={sede.id} value={sede.id}>{sede.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              onClick={handleSave} 
              className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
              disabled={saving || !selectedSede}
              data-testid="save-capacity-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : !selectedSede ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-64 text-center">
              <Settings className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">Selecciona una sede para configurar la capacidad</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {DAYS.map((day, index) => (
              <Card key={day.value} className="border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-sm flex items-center justify-center text-sm font-bold
                      ${formData[index]?.is_redistribution_day ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`}>
                      {day.label.charAt(0)}
                    </div>
                    {day.label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Capacidad máxima</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData[index]?.max_capacity || 50}
                        onChange={(e) => handleChange(index, 'max_capacity', parseInt(e.target.value) || 1)}
                        className="rounded-sm"
                        data-testid={`capacity-${day.value}-max`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Límite alta demanda (%)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={formData[index]?.high_demand_limit_percent || 70}
                        onChange={(e) => handleChange(index, 'high_demand_limit_percent', parseInt(e.target.value) || 70)}
                        className="rounded-sm"
                        data-testid={`capacity-${day.value}-limit`}
                      />
                      <p className="text-xs text-slate-500">
                        Cupo efectivo: {Math.round((formData[index]?.max_capacity || 50) * (formData[index]?.high_demand_limit_percent || 70) / 100)} personas
                      </p>
                    </div>
                    <div className="flex items-center gap-3 pt-6">
                      <Switch
                        checked={formData[index]?.is_redistribution_day || false}
                        onCheckedChange={(checked) => handleChange(index, 'is_redistribution_day', checked)}
                        data-testid={`capacity-${day.value}-redistribution`}
                      />
                      <Label className="text-sm">Día de redistribución</Label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CapacityPage;
